import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import ApiService from '../services/api.service';

const ApiTest = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  console.log('🧪 [ApiTest] Component mounting...');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Test results
  const [organizations, setOrganizations] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [drift, setDrift] = useState([]);
  const [driftRaw, setDriftRaw] = useState(null);
  const [typingCenters, setTypingCenters] = useState([]);
  const [driftTcId, setDriftTcId] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');

  // API responses for debugging
  const [apiResponses, setApiResponses] = useState({
    organizations: null,
    tickets: null
  });

  const [testResults, setTestResults] = useState({
    organizations: null,
    tickets: null,
    wallet: null,
    drift: null
  });

  const fetchOrganizations = async () => {
    console.log('📡 [ApiTest] fetchOrganizations called');
    setLoading(true);
    setError(null);

    try {
      console.log('📡 [ApiTest] Calling API: getOrganizationCustomers()');
      const response = await ApiService.getOrganizationCustomers();

      console.log('Organizations API Response:', response);
      setApiResponses(prev => ({ ...prev, organizations: JSON.stringify(response.data, null, 2) }));

      if (response.data && response.data.success) {
        const orgsData = response.data.data || [];
        setOrganizations(orgsData);
        setTestResults(prev => ({
          ...prev,
          organizations: {
            success: true,
            count: orgsData.length,
            message: `Successfully fetched ${orgsData.length} organizations`
          }
        }));

        // Auto-select first organization for further tests
        if (orgsData.length > 0 && !selectedOrgId) {
          setSelectedOrgId(orgsData[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      const errorMsg = err.response
        ? `API Error ${err.response.status}: ${JSON.stringify(err.response.data)}`
        : err.request
        ? 'No response from server. Check if API is running and CORS is configured.'
        : err.message;

      setError(errorMsg);
      setTestResults(prev => ({
        ...prev,
        organizations: { success: false, message: errorMsg }
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Fetching tickets for organization:', selectedOrgId);
      const response = await ApiService.get(`/api/tickets?organizationId=${selectedOrgId}`);

      console.log('Tickets API Response:', response);
      setApiResponses(prev => ({ ...prev, tickets: JSON.stringify(response.data, null, 2) }));

      if (response.data) {
        // Handle different response structures: array, { data: [] }, { tickets: [] }, or { content: [] }
        const ticketsData = Array.isArray(response.data)
          ? response.data
          : response.data.data || response.data.tickets || response.data.content || [];
        setTickets(ticketsData);
        setTestResults(prev => ({
          ...prev,
          tickets: {
            success: true,
            count: ticketsData.length,
            message: `Successfully fetched ${ticketsData.length} tickets`
          }
        }));
      }
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      const errorMsg = err.response
        ? `API Error ${err.response.status}: ${JSON.stringify(err.response.data)}`
        : err.message;

      setError(errorMsg);
      setTestResults(prev => ({
        ...prev,
        tickets: { success: false, message: errorMsg }
      }));
    } finally {
      setLoading(false);
    }
  };

  // Wallet balance of the selected organization, read from Thaklis Main through the Managers backend
  const fetchWallet = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization first');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.get(`/api/organisations/${selectedOrgId}/wallet`);
      const data = response?.data?.data ?? null;
      setWallet(data);
      setTestResults(prev => ({
        ...prev,
        wallet: data
          ? { success: true, balance: data.balance, message: 'Successfully fetched the wallet' }
          : { success: false, message: 'No wallet returned (no wallet for this organization, or Thaklis Main not reachable)' }
      }));
    } catch (err) {
      const errorMsg = err.response
        ? `API Error ${err.response.status}: ${err.response.data?.error || err.response.data?.message || JSON.stringify(err.response.data)}`
        : err.message;
      setWallet(null);
      setError(errorMsg);
      setTestResults(prev => ({ ...prev, wallet: { success: false, message: errorMsg } }));
    } finally {
      setLoading(false);
    }
  };

  // Ledger drift (BE-23a): per typing centre, bank balances and customer wallets against the ledger.
  const fetchDrift = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.get('/api/ledger-ops/drift', { params: driftTcId ? { typingCenterId: driftTcId } : {} });
      const rows = response?.data?.data || [];
      setDrift(rows);
      setDriftRaw(response?.data ?? null);
      const outOfSync = rows.filter((r) => !r.in_sync).length;
      setTestResults(prev => ({
        ...prev,
        drift: { success: true, count: rows.length, outOfSync, message: `${rows.length} typing centres, ${outOfSync} out of sync` }
      }));
    } catch (err) {
      const errorMsg = err.response
        ? `API Error ${err.response.status}: ${err.response.data?.error || JSON.stringify(err.response.data)}`
        : err.message;
      setError(errorMsg);
      setTestResults(prev => ({ ...prev, drift: { success: false, message: errorMsg } }));
    } finally {
      setLoading(false);
    }
  };

  const runAllTests = async () => {
    await fetchOrganizations();
    // Wait a bit for organization to be selected
    setTimeout(async () => {
      if (selectedOrgId) {
        await fetchTickets();
        await fetchWallet();
      }
      await fetchDrift();
    }, 1000);
  };

  useEffect(() => {
    console.log('⚡ [ApiTest] useEffect running - Auto-fetching organizations');
    // Auto-fetch organizations on component mount
    fetchOrganizations();
    // Typing centres for the drift picker and names (platform admin only; others get none)
    ApiService.getTypingCenters()
      .then((res) => setTypingCenters(res?.data?.data || res?.data || []))
      .catch(() => setTypingCenters([]));

    return () => {
      console.log('🔻 [ApiTest] Component unmounting...');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
        Thaklis API Integration Test
      </Typography>

      {/* Test Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.875rem' }}>Select Organization</InputLabel>
                <Select
                  value={selectedOrgId}
                  label="Select Organization"
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  {organizations.map(org => (
                    <MenuItem key={org.id} value={org.id} sx={{ fontSize: '0.875rem' }}>
                      {org.name} ({org.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={runAllTests}
                  disabled={loading}
                  sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#0E6B6B' } }}
                >
                  Run All Tests
                </Button>
                <Button
                  variant="outlined"
                  onClick={fetchOrganizations}
                  disabled={loading}
                  sx={{ borderColor: 'primary.main', color: 'secondary.main' }}
                >
                  Test Organizations
                </Button>
                <Button
                  variant="outlined"
                  onClick={fetchTickets}
                  disabled={loading || !selectedOrgId}
                  sx={{ borderColor: 'primary.main', color: 'secondary.main' }}
                >
                  Test Tickets
                </Button>
                <Button
                  variant="outlined"
                  onClick={fetchWallet}
                  disabled={loading || !selectedOrgId}
                  sx={{ borderColor: 'primary.main', color: 'secondary.main' }}
                >
                  Test Wallet
                </Button>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Drift: typing centre</InputLabel>
                  <Select
                    value={driftTcId}
                    label="Drift: typing centre"
                    onChange={(e) => setDriftTcId(e.target.value)}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All typing centres</MenuItem>
                    {typingCenters.map((tc) => (
                      <MenuItem key={tc.id} value={tc.id} sx={{ fontSize: '0.875rem' }}>{tc.name} ({tc.code})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  onClick={fetchDrift}
                  disabled={loading}
                  sx={{ borderColor: 'primary.main', color: 'secondary.main' }}
                >
                  Check Ledger Drift
                </Button>
                {loading && <CircularProgress size={24} />}
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              <strong>API Base URL:</strong> {process.env.REACT_APP_THAKLIS_API_URL || window.location.origin} (Managers Backend → Thaklis API)
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              <strong>Authentication:</strong> JWT Token (via Managers login)
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Test Results Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: testResults.organizations?.success ? '#e8f5e9' : '#ffebee' }}>
            <CardContent>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', display: 'block', mb: 0.5 }}>
                Organizations
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: testResults.organizations?.success ? '#73BF69' : '#F2495C', fontSize: '1rem' }}>
                {testResults.organizations ? (testResults.organizations.success ? `✓ ${testResults.organizations.count}` : '✗ Failed') : '- Pending'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: testResults.tickets?.success ? '#e8f5e9' : testResults.tickets?.success === false ? '#ffebee' : '#f5f5f5' }}>
            <CardContent>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', display: 'block', mb: 0.5 }}>
                Tickets
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: testResults.tickets?.success ? '#73BF69' : testResults.tickets?.success === false ? '#F2495C' : '#666', fontSize: '1rem' }}>
                {testResults.tickets ? (testResults.tickets.success ? `✓ ${testResults.tickets.count}` : '✗ Failed') : '- Pending'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: testResults.wallet?.success ? '#e8f5e9' : testResults.wallet?.success === false ? '#ffebee' : '#f5f5f5' }}>
            <CardContent>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', display: 'block', mb: 0.5 }}>
                Wallet
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: testResults.wallet?.success ? '#73BF69' : testResults.wallet?.success === false ? '#F2495C' : '#666', fontSize: '1rem' }}>
                {testResults.wallet ? (testResults.wallet.success ? `✓ AED ${Number(testResults.wallet.balance || 0).toFixed(2)}` : '✗ Failed') : '- Pending'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: testResults.drift?.success ? (testResults.drift.outOfSync ? '#fff3e0' : '#e8f5e9') : testResults.drift?.success === false ? '#ffebee' : '#f5f5f5' }}>
            <CardContent>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', display: 'block', mb: 0.5 }}>
                Ledger Drift
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: testResults.drift?.success ? (testResults.drift.outOfSync ? '#e65100' : '#73BF69') : testResults.drift?.success === false ? '#F2495C' : '#666', fontSize: '1rem' }}>
                {testResults.drift ? (testResults.drift.success ? (testResults.drift.outOfSync ? `⚠ ${testResults.drift.outOfSync} out of sync` : '✓ In sync') : '✗ Failed') : '- Pending'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabbed Data Display */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label={`Organizations (${organizations.length})`} sx={{ fontSize: '0.875rem' }} />
            <Tab label={`Tickets (${tickets.length})`} sx={{ fontSize: '0.875rem' }} />
            <Tab label="Wallet" sx={{ fontSize: '0.875rem' }} />
            <Tab label="Ledger Drift" sx={{ fontSize: '0.875rem' }} />
          </Tabs>
        </Box>

        {/* Tab 0: Organizations */}
        {currentTab === 0 && (
          <CardContent>
            {organizations.length > 0 ? (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1rem' }}>
                  Organizations Data
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {organizations.slice(0, 10).map((org) => (
                        <TableRow key={org.id} hover>
                          <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{org.code || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{org.name}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>
                            <Chip label={org.type} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{org.email || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>
                            <Chip label={org.active ? 'Active' : 'Inactive'} size="small" color={org.active ? 'success' : 'default'} sx={{ fontSize: '0.75rem' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {apiResponses.organizations && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>Raw JSON Response (first 3 items):</Typography>
                    <Box component="pre" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, overflow: 'auto', maxHeight: 300, fontSize: '0.75rem' }}>
                      {(() => {
                        try {
                          const parsed = JSON.parse(apiResponses.organizations);
                          const dataArray = parsed.data || parsed;
                          const sliced = Array.isArray(dataArray) ? dataArray.slice(0, 3) : dataArray;
                          return JSON.stringify(sliced, null, 2);
                        } catch (e) {
                          return apiResponses.organizations;
                        }
                      })()}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>
                No organizations data. Click "Test Organizations" to fetch.
              </Typography>
            )}
          </CardContent>
        )}

        {/* Tab 1: Tickets */}
        {currentTab === 1 && (
          <CardContent>
            {tickets.length > 0 ? (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1rem' }}>
                  Tickets Data
                </Typography>
                <Box component="pre" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, overflow: 'auto', maxHeight: 500, fontSize: '0.75rem' }}>
                  {JSON.stringify(tickets.slice(0, 5), null, 2)}
                </Box>
              </>
            ) : (
              <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>
                {selectedOrgId ? 'No tickets data. Click "Test Tickets" to fetch.' : 'Please select an organization first.'}
              </Typography>
            )}
          </CardContent>
        )}

        {/* Tab 2: Wallet */}
        {currentTab === 2 && (
          <CardContent>
            {wallet ? (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1rem' }}>
                  {wallet.organisationName || 'Wallet'}: AED {Number(wallet.balance || 0).toFixed(2)}
                </Typography>
                <Box component="pre" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, overflow: 'auto', maxHeight: 300, fontSize: '0.75rem' }}>
                  {JSON.stringify(wallet, null, 2)}
                </Box>
              </>
            ) : (
              <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>
                {selectedOrgId ? (testResults.wallet?.message || 'No wallet data. Click "Test Wallet" to fetch.') : 'Please select an organization first.'}
              </Typography>
            )}
          </CardContent>
        )}

        {/* Tab 3: Ledger Drift */}
        {currentTab === 3 && (
          <CardContent>
            <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
              Results are only meaningful after the ledger cutover — before it the ledger has no opening balances, so every
              account shows a difference. Thaklis ticket payments (BE-22b, not built) keep showing a bank difference until
              they post through the ledger.
            </Alert>
            {drift.length === 0 ? (
              <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>
                No drift report yet. Click "Check Ledger Drift" (platform admin).
              </Typography>
            ) : drift.map((tc) => (
              <Box key={tc.typing_center_id} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {(() => {
                    const named = typingCenters.find((t) => t.id === tc.typing_center_id);
                    return named
                      ? <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{named.name} ({named.code})</Typography>
                      : <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', fontFamily: 'monospace' }}>{tc.typing_center_id}</Typography>;
                  })()}
                  <Chip label={tc.in_sync ? 'In sync' : 'Out of sync'} size="small" color={tc.in_sync ? 'success' : 'warning'} sx={{ fontSize: '0.75rem' }} />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>Account</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>Ledger</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>Balance</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>Difference</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.8rem' }}>Customer advances (wallets)</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{Number(tc.customer_advances?.ledger || 0).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{Number(tc.customer_advances?.wallets || 0).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: Number(tc.customer_advances?.difference || 0) !== 0 ? 'error.main' : 'text.primary' }}>
                          {Number(tc.customer_advances?.difference || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                      {(tc.bank_accounts || []).map((b) => (
                        <TableRow key={b.bank_account_id}>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{b.name}{b.deleted ? ' (deleted)' : ''}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{Number(b.ledger || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{Number(b.balance || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, color: Number(b.difference || 0) !== 0 ? 'error.main' : 'text.primary' }}>
                            {Number(b.difference || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
            {driftRaw && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>Raw JSON Response:</Typography>
                <Box component="pre" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, overflow: 'auto', maxHeight: 300, fontSize: '0.75rem' }}>
                  {JSON.stringify(driftRaw, null, 2)}
                </Box>
              </Box>
            )}
          </CardContent>
        )}
      </Card>
    </Box>
  );
};

export default ApiTest;
